import { useState, useRef, useEffect, useMemo } from 'react';
import { RiSearchLine, RiArrowDownSLine, RiCloseLine, RiMapPin2Line } from '@remixicon/react';
import type { SogralDestination } from '../../types/sogral';
import { cn } from '../../lib/utils';

function norm(s: string) {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[-_'\s]+/g, ' ')
    .trim();
}

function toTitle(s: string) {
  return s.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

interface WilayaGroup {
  wilayaNum: number;
  wilayaName: string;
  capitalCode: string;
  communes: SogralDestination[];
}

function buildGroups(destinations: SogralDestination[]): WilayaGroup[] {
  const byWilaya = new Map<number, SogralDestination[]>();
  for (const d of destinations) {
    if (typeof d.P4 !== 'number' || !d.P1) continue;
    if (!byWilaya.has(d.P4)) byWilaya.set(d.P4, []);
    byWilaya.get(d.P4)!.push(d);
  }

  const groups: WilayaGroup[] = [];
  for (const [wilayaNum, items] of byWilaya) {
    const normWilaya = norm(items[0]?.P5 || '');
    const capital =
      items.find((d) => norm(d.P2) === normWilaya) ||
      items.find((d) => d.P1?.endsWith('001')) ||
      [...items].sort((a, b) => a.P1.localeCompare(b.P1))[0];

    const communes = items
      .filter((d) => d.P1 !== capital?.P1)
      .sort((a, b) => (a.P2 || '').localeCompare(b.P2 || '', 'fr'));

    groups.push({
      wilayaNum,
      wilayaName: capital?.P5 || `Wilaya ${wilayaNum}`,
      capitalCode: capital?.P1 || '',
      communes,
    });
  }

  return groups.sort((a, b) => a.wilayaName.localeCompare(b.wilayaName, 'fr'));
}

interface Props {
  destinations: SogralDestination[];
  value: string;
  onChange: (code: string) => void;
  placeholder?: string;
  variant?: 'light' | 'dark';
}

export function DestinationCombobox({
  destinations,
  value,
  onChange,
  placeholder = 'Choisir une destination',
  variant = 'light',
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDark = variant === 'dark';

  const groups = useMemo(() => buildGroups(destinations), [destinations]);

  const selected = destinations.find((d) => d.P1 === value);
  const selectedLabel = selected
    ? norm(selected.P2) === norm(selected.P5)
      ? selected.P5
      : `${toTitle(selected.P2)}, ${selected.P5}`
    : '';

  const filteredGroups = useMemo(() => {
    if (!query.trim()) return groups;
    const q = norm(query);
    return groups
      .map((g) => {
        const wilayaMatch =
          norm(g.wilayaName).includes(q) ||
          g.communes.some((c) => norm(c.P2).includes(q));
        if (!wilayaMatch) return null;
        const matchingCommunes = norm(g.wilayaName).includes(q)
          ? g.communes
          : g.communes.filter((c) => norm(c.P2).includes(q));
        return { ...g, communes: matchingCommunes };
      })
      .filter((g): g is WilayaGroup => g !== null);
  }, [groups, query]);

  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  function openDropdown() {
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 30);
  }

  function select(code: string) {
    onChange(code);
    setOpen(false);
    setQuery('');
  }

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openDropdown())}
        className={cn(
          'w-full h-[52px] rounded-2xl flex items-center gap-3 px-4 text-left transition-all',
          isDark
            ? 'bg-white/15 border border-white/20 text-white hover:bg-white/20'
            : 'bg-white shadow-[0_2px_12px_rgba(0,0,0,0.12)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.16)]',
        )}
      >
        <RiMapPin2Line
          className={cn('h-4 w-4 shrink-0', isDark ? 'text-white/60' : 'text-ink-muted')}
        />
        <span
          className={cn(
            'flex-1 font-display text-[15px] truncate',
            selectedLabel
              ? isDark ? 'text-white font-semibold' : 'text-ink font-semibold'
              : isDark ? 'text-white/50' : 'text-ink-muted',
          )}
        >
          {selectedLabel || placeholder}
        </span>
        <RiArrowDownSLine
          className={cn(
            'h-5 w-5 shrink-0 transition-transform duration-200',
            isDark ? 'text-white/60' : 'text-ink-muted',
            open && 'rotate-180',
          )}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute z-[200] top-full mt-2 left-0 right-0 rounded-2xl shadow-2xl overflow-hidden bg-white border border-line-light"
          style={{ maxHeight: 340 }}
        >
          {/* Search */}
          <div className="flex items-center gap-2.5 px-4 py-3 border-b border-line-light bg-white sticky top-0">
            <RiSearchLine className="h-4 w-4 text-ink-muted shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher une ville…"
              className="flex-1 font-display text-[14px] text-ink placeholder:text-ink-muted outline-none bg-transparent"
            />
            {query && (
              <button
                type="button"
                onClick={() => { setQuery(''); inputRef.current?.focus(); }}
                className="text-ink-muted hover:text-ink transition-colors"
              >
                <RiCloseLine className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* List */}
          <div className="overflow-y-auto" style={{ maxHeight: 280 }}>
            {filteredGroups.length === 0 && (
              <p className="px-4 py-6 text-center font-display text-[13px] text-ink-muted">
                Aucun résultat
              </p>
            )}
            {filteredGroups.map((g) => (
              <div key={g.wilayaNum}>
                {/* Wilaya row */}
                <button
                  type="button"
                  onClick={() => select(g.capitalCode)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                    value === g.capitalCode
                      ? 'bg-brand/8 text-brand'
                      : 'hover:bg-surface-page text-ink',
                  )}
                >
                  <span className="font-display font-semibold text-[14px]">{g.wilayaName}</span>
                  {value === g.capitalCode && (
                    <span className="ms-auto font-display text-[11px] font-bold text-brand">✓</span>
                  )}
                </button>

                {/* Communes */}
                {g.communes.map((c) => (
                  <button
                    key={c.P1}
                    type="button"
                    onClick={() => select(c.P1)}
                    className={cn(
                      'w-full flex items-center gap-2.5 ps-9 pe-4 py-2 text-left transition-colors',
                      value === c.P1
                        ? 'bg-brand/8 text-brand'
                        : 'hover:bg-surface-page text-ink-secondary',
                    )}
                  >
                    <span className="w-1 h-1 rounded-full bg-current opacity-40 shrink-0" />
                    <span className="font-display text-[13px]">{toTitle(c.P2)}</span>
                    {value === c.P1 && (
                      <span className="ms-auto font-display text-[11px] font-bold text-brand">✓</span>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
